import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiMutation } from '@/api/client';
import type { HelioScoreInput, HelioScoreResult } from '@/api/types';

const SCORE_INPUT_DEBOUNCE_MS = 400;

function scoreInputKey(input: HelioScoreInput | null): string | null {
  if (!input?.postcode?.trim()) return null;
  return JSON.stringify(input);
}

export function useHelioScore(input: HelioScoreInput | null) {
  const inputRef = useRef(input);
  inputRef.current = input;
  const key = scoreInputKey(input);
  const [debouncedInput, setDebouncedInput] = useState<HelioScoreInput | null>(null);

  useEffect(() => {
    if (key == null) {
      setDebouncedInput(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setDebouncedInput(inputRef.current);
    }, SCORE_INPUT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [key]);

  const enabled = Boolean(debouncedInput?.postcode?.trim());

  return useQuery<HelioScoreResult>({
    queryKey: ['helio-score', debouncedInput],
    queryFn: () => apiMutation<HelioScoreResult>('/api/helio-score/preliminary', debouncedInput),
    enabled,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** @deprecated Use useHelioScore */
export const useHelioScorePreliminary = useHelioScore;
